import React from 'react'
import { View, ViewStyle, Image, StyleSheet } from 'react-native'
import { DS } from '../../design-system'
import { PieChart } from './PieChart'
import Cards from './Cards.svg'
import Chips from './Chips.png'
import { useStore } from '../../models'
import { fontFamily } from '../../theme'
import { Card, CardProps } from '../../components/TempNewDesign/Card'
import { CardString } from 'pokersolver'
import { observer } from 'mobx-react-lite'
import { formatDaily10Score } from '../../utils/daily10Share'

// Stats arrive already formatted (e.g. "58.5K"); only shorten plain numbers
const formatCompactNumber = (value: string | number) => {
  const n = Number(String(value).replace(/,/g, ''))
  return Number.isFinite(n) ? formatDaily10Score(n) : String(value)
}

export const Wrapper: React.FC<{
  children?: React.ReactNode
  style?: ViewStyle | ViewStyle[]
}> = ({ children, style = {} }) => {
  const styles = wrapperStyles
  return <View style={[styles.container, style]}>{children}</View>
}

const wrapperStyles = StyleSheet.create({
  container: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[8]
  }
})

interface BestHandProps {
  bestHand: CardString[]
  bestHandName?: string
  bestHandDate?: string
}

const BestHand: React.FC<BestHandProps> = ({ bestHand, bestHandName, bestHandDate }) => {
  const styles = bestHandStyles
  return (
    <>
      <View style={styles.container}>
        {bestHand.map((card, index) => (
          <Card
            {...cardStyles}
            key={card}
            card={card}
            cardStyle={[
              cardStyles.cardStyle,
              {
                zIndex: index,
                transform: rotations[index]
              }
            ]}
          />
        ))}
      </View>
      <View style={{ gap: 4, alignItems: 'center' }}>
        <DS.Text variant="Caption/Medium">Best Hand: {bestHandName}</DS.Text>
        <DS.Text style={{ textAlign: 'center' }} variant="Caption/Regular" color="Brand/Dark Grey">
          Most formidable poker hand you've ever played with and won
        </DS.Text>
        <DS.Text variant="Mini/Medium" color="Brand/Dark Grey">
          {bestHandDate}
        </DS.Text>
      </View>
    </>
  )
}

const bestHandStyles = StyleSheet.create({
  container: {
    top: -20,
    flexDirection: 'row',
    position: 'absolute'
  },
  textContainer: { gap: 4, alignItems: 'center' },
  description: { textAlign: 'center' }
})

const ChartWrapper: React.FC<{
  title: string
  description: string
  chart: React.ReactNode
}> = ({ chart, title, description }) => {
  const styles = chartWrapperStyles

  const twoLinesText = (text: string) => {
    const lines = text.split('\n')
    if (lines.length < 2) {
      const words = text.split(' ')
      const mid = Math.ceil(words.length / 2)
      return `${words.slice(0, mid).join(' ')}\n${words.slice(mid).join(' ')}`
    }
    return text
  }

  return (
    <View style={styles.container}>
      {chart}
      <View style={styles.textContainer}>
        <DS.Text style={styles.title} variant="Mini/Medium">
          {twoLinesText(title)}
        </DS.Text>
        <DS.Text style={styles.description} variant="Mini/Regular" color="Brand/Dark Grey">
          {description}
        </DS.Text>
      </View>
    </View>
  )
}

const chartWrapperStyles = StyleSheet.create({
  container: {
    gap: DS.spacing[8],
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  textContainer: {
    gap: DS.spacing[4],
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  },
  title: { textAlign: 'center', textTransform: 'uppercase' },
  description: { textAlign: 'center' }
})

const cardStyles: CardProps = {
  fontSize: 16,
  cardStyle: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[4],
    shadowColor: DS.palette['Global/Black'],
    shadowOffset: {
      width: 1,
      height: 4
    },
    elevation: 1,
    shadowOpacity: 0.1,
    shadowRadius: DS.spacing[8],
    borderWidth: 1,
    borderColor: DS.palette['Global/White']
  },
  cardTextStyle: { fontFamily: fontFamily.FeatureDeckRegular }
}

const rotations = [
  [{ rotate: '-30deg' }, { translateY: 16 }, { translateX: -4 }],
  [{ rotate: '-15deg' }, { translateY: 4 }],
  [{ rotate: '0deg' }],
  [{ rotate: '15deg' }, { translateY: 4 }],
  [{ rotate: '30deg' }, { translateY: 16 }, { translateX: 4 }]
]

export const PlayerStats = observer(() => {
  const {
    game: {
      myHandsStats: { totalNumberOfHands, numberOfHandsPlayed, numberOfHandsWon, preflopFold, percentage },
      myAllTimeBest: { allTimeBiggestChipsWon }
    }
  } = useStore()
  const styles = playerStatsStyles
  return (
    <>
      <Wrapper style={styles.container}>
        <View style={styles.chartsRow}>
          <ChartWrapper
            chart={
              <PieChart
                centerComponent={<DS.Text variant="Heading 5/Medium">{formatCompactNumber(totalNumberOfHands)}</DS.Text>}
                size={72}
                strokeWidth={6}
                data={[{ percent: 1, color: DS.palette['Brand/Purple'] }]}
              />
            }
            title="TOTAL HANDS"
            description="Total hands you've been dealt"
          />
          <ChartWrapper
            chart={
              <PieChart
                centerComponent={<DS.Text variant="Heading 5/Medium">{formatCompactNumber(numberOfHandsPlayed)}</DS.Text>}
                size={72}
                strokeWidth={6}
                data={[
                  { percent: percentage.numberOfHandsPlayed, color: DS.palette['Brand/Pink'] },
                  { percent: 1 - percentage.numberOfHandsPlayed, color: DS.palette['Brand/Cream'] }
                ]}
              />
            }
            title="HANDS PLAYED"
            description="Total hands you've acted on"
          />
          <ChartWrapper
            chart={
              <PieChart
                centerComponent={<DS.Text variant="Heading 5/Medium">{formatCompactNumber(numberOfHandsWon)}</DS.Text>}
                size={72}
                strokeWidth={6}
                data={[
                  { percent: percentage.numberOfHandsWon, color: DS.palette['Brand/Orange'] },
                  { percent: 1 - percentage.numberOfHandsWon, color: DS.palette['Brand/Cream'] }
                ]}
              />
            }
            title="HANDS WON"
            description="Total hands you've won"
          />
        </View>
        <View style={styles.divider} />
        <View style={styles.row}>
          <PieChart
            centerComponent={<Cards />}
            size={48}
            strokeWidth={4}
            data={[
              { percent: percentage.preflopFold, color: DS.palette['Brand/Blue'] },
              { percent: 1 - percentage.preflopFold, color: DS.palette['Brand/Cream'] }
            ]}
          />
          <View style={styles.rowText}>
            <DS.Text variant="Mini/Medium">{formatCompactNumber(preflopFold)} PRE-FLOP FOLDS</DS.Text>
            <DS.Text variant="Mini/Regular" color="Brand/Dark Grey">
              Hands you've folded before the flop
            </DS.Text>
          </View>
          <DS.Text variant="Heading 5/Medium">{Math.round(percentage.preflopFold * 100)}%</DS.Text>
        </View>
        <View style={styles.divider} />
        <View style={styles.row}>
          <Image style={styles.chipsImg} source={Chips} />
          <View style={styles.rowText}>
            <DS.Text variant="Mini/Medium">BIGGEST WIN</DS.Text>
            <DS.Text variant="Mini/Regular" color="Brand/Dark Grey">
              Largest pot you've ever claimed
            </DS.Text>
          </View>
          <DS.Text variant="Heading 5/Medium">{formatCompactNumber(allTimeBiggestChipsWon)} Chips</DS.Text>
        </View>
      </Wrapper>
    </>
  )
})

export const PlayerBestHand = observer(() => {
  const {
    game: {
      myAllTimeBest: { bestHand, bestHandDate, bestHandName }
    }
  } = useStore()
  if (bestHand.length === 0) return null
  return (
    <Wrapper style={playerStatsStyles.bestHandWrapper}>
      <BestHand bestHand={bestHand} bestHandDate={bestHandDate} bestHandName={bestHandName} />
    </Wrapper>
  )
})

const playerStatsStyles = StyleSheet.create({
  container: { paddingHorizontal: DS.spacing[16] },
  chartsRow: {
    flexDirection: 'row',
    paddingVertical: DS.spacing[16],
    gap: DS.spacing[8]
  },
  divider: { height: 1, backgroundColor: DS.palette['Brand/Cream'] },
  row: {
    paddingVertical: DS.spacing[16],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: DS.spacing[16]
  },
  rowText: { gap: DS.spacing[4], flex: 1 },
  chipsImg: { width: 48, height: 48 },
  bestHandWrapper: {
    paddingHorizontal: DS.spacing[32],
    paddingTop: DS.spacing[48],
    paddingBottom: DS.spacing[16],
    alignItems: 'center'
  }
})
